import { AssetRecordType } from "@tldraw/tlschema";
import { FileHelpers } from "@tldraw/utils";
import type { Editor } from "./canvas";
import {
  LIBRARY_MAX_BYTES,
  validateLibraryBundle,
  type LibraryBundle,
} from "../shared/library";
import { sheetAt } from "./sheet-operations";
import { uploadFile, type Room } from "./api";
export async function captureLibrarySelection(
  editor: Editor,
): Promise<LibraryBundle> {
  const selection = editor.getSelectedShapeIds();
  if (!selection.length)
    throw new Error("Select the objects you want to save.");
  if (
    selection.some((id) => {
      const s = editor.getShape(id);
      return !s || editor.isShapeHidden(s) || editor.isShapeOrAncestorLocked(s);
    })
  )
    throw new Error("Select visible, unlocked objects.");
  const raw = editor.getContentFromCurrentPage(selection);
  if (!raw) throw new Error("Select drawing objects to save.");
  if (
    raw.shapes.some(
      (shape) =>
        editor.isShapeHidden(shape) || editor.isShapeOrAncestorLocked(shape),
    )
  )
    throw new Error(
      "The selection contains hidden or locked objects. Select only visible, unlocked contents.",
    );
  const bounds = editor.getSelectionPageBounds();
  const original = JSON.stringify(raw.shapes);
  const content = structuredClone(raw),
    images: Record<string, string> = {};
  let size = 0;
  if (content.shapes.length > 100 || content.assets.length > 20)
    throw new Error("Save up to 100 objects and 20 images at a time.");
  for (const asset of content.assets) {
    if (asset.type !== "image" || !asset.props.src)
      throw new Error("Save ready still images and drawing objects.");
    const src = new URL(asset.props.src, location.origin);
    if (
      src.origin !== location.origin ||
      !src.pathname.startsWith("/api/rooms/")
    )
      throw new Error("Import this image into the project before saving it.");
    const response = await fetch(src, { signal: AbortSignal.timeout(30000) });
    if (!response.ok)
      throw new Error("An image could not be copied. Try again.");
    const reader = response.body?.getReader();
    if (!reader) throw new Error("Image unavailable.");
    const parts: Uint8Array[] = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > 5_500_000) {
        await reader.cancel();
        throw new Error(
          "The images exceed the 8 MB library item limit. Save a smaller selection.",
        );
      }
      parts.push(value);
    }
    const mime =
      response.headers.get("Content-Type")?.split(";")[0] ||
      asset.props.mimeType ||
      "";
    if (!["image/png", "image/jpeg", "image/webp"].includes(mime))
      throw new Error("Library images must be PNG, JPEG or WebP.");
    images[asset.id] = await FileHelpers.blobToDataUrl(
      new Blob(parts as BlobPart[], { type: mime }),
    );
    asset.props = { ...asset.props, src: null, mimeType: mime };
    asset.meta = {};
  }
  // Reusable copies must not retain source-room downloads, generated-job IDs or PDF navigation.
  for (const shape of content.shapes) {
    if (shape.type === "image") shape.meta = {};
    if (shape.type === "document" || shape.type === "table")
      shape.props.source = "";
    shape.isLocked = false;
  }
  const bundle: LibraryBundle = { version: 1, content, images };
  validateLibraryBundle(bundle);
  if (bounds && bounds.w >= 0 && bounds.h >= 0) {
    try {
      // Straight lines can have zero width or height. Export padding makes them
      // renderable; keep positive preview dimensions without changing the shapes.
      const width = Math.max(1, bounds.w), height = Math.max(1, bounds.h);
      const rendered = await editor.toImage(selection, {
        format: "png",
        bounds,
        padding: 12,
        background: true,
        scale: Math.min(1, 420 / (Math.max(width, height) + 24)),
        pixelRatio: 1,
      });
      const image = await FileHelpers.blobToDataUrl(rendered.blob);
      if (image.length <= 180_000)
        bundle.preview = { image, width, height };
    } catch {
      /* A missing thumbnail must not prevent saving valid content. */
    }
  }
  if (
    JSON.stringify(editor.getContentFromCurrentPage(selection)?.shapes) !==
    original
  )
    throw new Error(
      "The selection changed while preparing it. Try saving again.",
    );
  if (
    new TextEncoder().encode(JSON.stringify(bundle)).length > LIBRARY_MAX_BYTES
  )
    throw new Error("This library item exceeds 8 MB.");
  return validateLibraryBundle(bundle);
}
export async function insertLibraryItem(
  editor: Editor,
  room: Room,
  bundle: LibraryBundle,
  point: { x: number; y: number },
  signal: AbortSignal,
) {
  const target = sheetAt(editor, point);
  if (
    target &&
    (editor.isShapeHidden(target) || editor.isShapeOrAncestorLocked(target))
  )
    throw new Error("Choose the canvas or an unlocked sheet to place this copy.");
  const valid = validateLibraryBundle(bundle),
    content = structuredClone(valid.content);
  const replacements = new Map<string, string>();
  for (const asset of content.assets) {
    if (asset.type !== "image") continue;
    signal.throwIfAborted();
    const match = /^data:([^;]+);base64,(.+)$/.exec(valid.images[asset.id])!;
    const bytes = Uint8Array.from(atob(match[2]), (c) => c.charCodeAt(0));
    const file = new File([bytes], asset.props.name || "library.png", {
      type: match[1],
    });
    const uploaded = await uploadFile(
      room.id,
      file,
      signal,
      room.platform === "cloudflare",
    );
    const id = AssetRecordType.createId();
    replacements.set(asset.id, id);
    asset.props.src = uploaded.src;
    asset.meta = uploaded.image ? { imagePreviews: true } : {};
  }
  content.assets = content.assets.map((asset) => ({
    ...asset,
    id: (replacements.get(asset.id) ?? asset.id) as typeof asset.id,
  }));
  for (const shape of content.shapes)
    if (shape.type === "image" && shape.props.assetId)
      shape.props.assetId = (replacements.get(shape.props.assetId) ??
        shape.props.assetId) as typeof shape.props.assetId;
  signal.throwIfAborted();
  if (editor.getIsReadonly()) throw new Error("This board is now read-only.");
  const currentTarget = sheetAt(editor, point);
  if (
    currentTarget?.id !== target?.id ||
    (currentTarget &&
      (editor.isShapeHidden(currentTarget) ||
        editor.isShapeOrAncestorLocked(currentTarget)))
  )
    throw new Error("The destination changed. Choose a position again.");
  editor.markHistoryStoppingPoint("Insert library item");
  editor.run(() => {
    editor.putContentOntoCurrentPage(content, { point, select: true });
    // Outside a sheet, keep the copy on the canvas even over other containers.
    if (!currentTarget)
      editor.reparentShapes(editor.getSelectedShapeIds(), editor.getCurrentPageId());
  });
  editor.setCurrentTool("select");
  editor.focus();
}
